<script setup lang="ts">
import { SortMode } from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import { Filters } from "~/core/models/filters";
import { formatIsoDate } from "~/core/utils/date";
import { useFetchCampaigns } from "~/modules/campaign/composables/useFetchCampaigns";
import { PATH_CAMPAIGNS_UID } from "~/modules/campaign/constants";
import type { CampaignOperationMgr } from "~/modules/campaign/models/CampaignOperationMgr.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";

const { currentOrganization } = storeToRefs(useOrganizationStore());
const { selectedSectorCodes } = storeToRefs(useSectorStore());
const {
  isLoading,
  error,
  results,
  filters,
  fetchData,
  setFilter,
  search,
  onSearch,
  onSortChange,
} = useFetchCampaigns({
  organizationCode: currentOrganization.value?.code,
});

const { t } = useI18n();
const router = useRouter();

async function loadData(page?: number, pageSize?: number) {
  setFilter(Filters.SECTORS, selectedSectorCodes.value || []);
  await fetchData(page, pageSize);
}

const searchQuery = computed({
  get() {
    return (filters.get(Filters.NAME) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.NAME, searchQuery);
  },
});

const onSelectCampaign = (row: CampaignOperationMgr) => {
  router.push({ name: PATH_CAMPAIGNS_UID, params: { uid: row.uid } });
};

const countDevicesFromOperationStatistics = (
  statistics?: Record<string, number>,
) => {
  if (statistics === undefined) {
    return 0;
  }
  return Object.values(statistics).reduce((acc, curr) => acc + curr, 0);
};

onSearch(loadData);

watch(selectedSectorCodes, () => {
  loadData();
});
</script>
<template>
  <div class="mb-4">
    <search-bar
      v-model:search-query="searchQuery"
      :search-placeholder="t('filters.searchByName')"
      @clear="search"
      @search="search"
    />
  </div>
  <lxc-table
    min-width="76rem"
    :is-loading="isLoading"
    :error="error?.toError()"
    :data="results?.results"
    :context="results?.pagination"
    :empty-text="t('campaign.list.empty')"
    clickable
    @row-click="onSelectCampaign"
    @change-page-and-page-size="loadData"
    @sort-change="onSortChange"
  >
    <lxc-table-column
      prop="name"
      :label="t('campaign.list.column.name.label')"
      :sort-mode="SortMode.CUSTOM"
      min-width="10rem"
    />
    <lxc-table-column
      :label="t('campaign.list.column.type.label')"
      width="15rem"
    >
      <template #default="scope">
        <campaign-operation-type :operation-types="scope.row.operationTypes" />
      </template>
    </lxc-table-column>
    <lxc-table-column
      prop="launchPlannedAt"
      :label="t('campaign.list.column.dates.label')"
      :sort-mode="SortMode.CUSTOM"
      width="15rem"
    >
      <template #default="scope">
        {{
          formatIsoDate(
            scope.row.launchPlannedAt,
            t("campaign.list.column.dates.format"),
          )
        }}
        →
        {{
          formatIsoDate(
            scope.row.expiredAt,
            t("campaign.list.column.dates.format"),
          )
        }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      prop="deviceCount"
      :label="t('campaign.list.column.numberOfDevice.label')"
      width="10rem"
    >
      <template #default="scope">
        {{
          t(
            "campaign.list.column.numberOfDevice.value",
            countDevicesFromOperationStatistics(scope.row.operationStatistics),
          )
        }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      prop="status"
      :label="t('campaign.list.column.status.label')"
      class="!py-0"
      width="12rem"
    >
      <template #default="scope">
        <campaign-operation-mgr-status-badge :status="scope.row.status" />
      </template>
    </lxc-table-column>
    <lxc-table-column
      prop="status"
      :label="t('campaign.list.column.operations')"
      class="!py-0"
      width="17rem"
      :no-tooltip="true"
    >
      <template #default="scope">
        <campaign-stats :operation-statistics="scope.row.operationStatistics" />
      </template>
    </lxc-table-column>
  </lxc-table>
</template>
